import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Card, CardBody, Col, Container, Input, InputGroup, InputGroupText, Row } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { getCheckin, clearCheckin, modalCheckin, getDashboard } from "src/store/actions"
import { useTranslation } from "react-i18next";
import TableCheck from "./components/TableCheck";
import ModalCheckin from "./components/ModalCheckin";
import NoSearchResult from "../../components/Common/NoSearchResult";
import usePermissions from "src/hooks/usePermissions";
import Select from "src/components/Common/Select";




const Checkin = () => {
	document.title = "Chekin List | Chekin board";
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { hasPermission } = usePermissions();
	const navigate = useNavigate();

	const rawData = useSelector((state: any) => state.Checkin.data || []);
	const daysInMonth = useSelector((state: any) => state.Checkin.daysInMonth || [])
	const loading = useSelector((state: any) => state.Checkin.loading || false);
	const isOpenModal = useSelector((state: any) => state.Checkin.isOpenModal || false);
	const teamsAll = useSelector((state: any) => state.dashboard || { data: [] });

	const data = useMemo(() => {
		if (Array.isArray(rawData)) return rawData;
		if (Array.isArray(rawData?.data)) return rawData.data;
		return [];
	}, [rawData]);

	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [selectedTeam, setSelectedTeam] = useState<any>(null);
	const defaultMonth = useMemo(() => String(new Date().getMonth() + 1), []);
	const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonth);

	const monthOptions = useMemo(() => ([
		{ value: "1", label: "January" },
		{ value: "2", label: "February" },
		{ value: "3", label: "March" },
		{ value: "4", label: "April" },
		{ value: "5", label: "May" },
		{ value: "6", label: "June" },
		{ value: "7", label: "July" },
		{ value: "8", label: "August" },
		{ value: "9", label: "September" },
		{ value: "10", label: "October" },
		{ value: "11", label: "November" },
		{ value: "12", label: "December" }
	]), []);

	const handleCheckinClicks = () => {
		dispatch(modalCheckin(true))
	};

	useEffect(() => {
		dispatch(getDashboard());

		return () => {
			dispatch(clearCheckin())
		};
	}, [dispatch]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchTerm.trim());
		}, 300);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	useEffect(() => {
		const filter: any = {};
		if (selectedMonth) {
			filter.month = selectedMonth;
		}
		if (selectedTeam?.value && selectedTeam.value > 0) {
			filter.team_id = selectedTeam.value;
		}
		if (debouncedSearch) {
			filter.search = debouncedSearch;
		}
		dispatch(getCheckin(filter));
	}, [dispatch, selectedMonth, selectedTeam, debouncedSearch]);

	const teamOptions = useMemo(() => {
		const raw = Array.isArray(teamsAll.data) ? teamsAll.data : [];
		const mapped = raw.map((team: any) => ({
			value: team?.value ?? team?.id,
			label: team?.label ?? team?.name,
		})).filter((team: any) => team.value != null && team.label != null);
		return [{ value: 0, label: "All teams" }, ...mapped];
	}, [teamsAll.data]);

	const handleResetFilters = () => {
		setSearchTerm("");
		setSelectedTeam(null);
		setSelectedMonth(defaultMonth);
	};

	const hasTeamFilter = Boolean(selectedTeam?.value && selectedTeam.value > 0);
	const hasSearchFilter = Boolean(searchTerm.trim());
	const isDefaultMonth = selectedMonth === defaultMonth;

//
// 	useEffect(() => {
// 		const permission = hasPermission('view-checkin');
// 		if (!permission) {
// 		navigate('/no-access');
// 		}
// }, [hasPermission, navigate]);

	return (
		<>
			<div className="page-content">
				<Container fluid>
					<Breadcrumbs title="Checkin" breadcrumbItem="Checkin" />
					<Row>
						<Col md={12}>
							<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
								<div className="d-flex flex-wrap align-items-center gap-2">
									<div style={{ minWidth: 260 }}>
										<InputGroup>
											<InputGroupText>
												<i className="bx bx-search-alt-2"></i>
											</InputGroupText>
											<Input
												value={searchTerm}
												onChange={(e) => setSearchTerm(e.target.value)}
												placeholder="Search by employee or ID"
												aria-label="Search employees"
											/>
											{searchTerm ? (
												<InputGroupText
													role="button"
													onClick={() => setSearchTerm("")}
													title="Clear search"
													style={{ cursor: "pointer" }}
												>
													<i className="bx bx-x"></i>
												</InputGroupText>
											) : null}
										</InputGroup>
									</div>
									<div style={{ minWidth: 200 }}>
										<select
											id="month"
											value={selectedMonth}
											onChange={(e) => setSelectedMonth(e.target.value)}
											className="form-select"
											aria-label="Select month"
										>
											{monthOptions.map((option) => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</div>
									<div style={{ minWidth: 220 }}>
										<Select
											isSearchable
											isClearable
											placeholder="All teams"
											options={teamOptions}
											onChange={(value: any) => setSelectedTeam(value || null)}
											value={selectedTeam}
										/>
									</div>
									<div>
										<button
											type="button"
											className="btn btn-outline-light"
											onClick={handleResetFilters}
											disabled={!hasSearchFilter && !hasTeamFilter && isDefaultMonth}
										>
											Reset
										</button>
									</div>
								</div>
								<div>
									<Link to="#" className="btn btn-light" onClick={handleCheckinClicks}>
										<i className="bx bx-plus me-1"></i> Add New Employee
									</Link>
								</div>
							</div>
						</Col>
					</Row>
					<Row>
						<Col lg="12">
							<Card>
								<CardBody className="border-bottom">
									<TableCheck data={data} daysInMonth={daysInMonth} />
									{!loading && data.length === 0 ? (<NoSearchResult />) : null}
									{loading ? (<NoSearchResult text="Loading..." />) : null}
								</CardBody>
							</Card>
						</Col>
					</Row>
					{isOpenModal && <ModalCheckin isOpen={true} />}
				</Container>
			</div>
		</>


	);

};

export default Checkin;
